import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  ISubscription,
  LogLevel,
} from '@microsoft/signalr';

import { LegacyError, throwIfGraphError } from './errors';
import {
  Command,
  CommandResult,
  Push,
  PushHandler,
  PushMessage,
  Query,
  QueryResult,
  Transport,
  Unsubscribe,
} from './types';

/**
 * SignalR transport for the One API generic graph, over the shared One hub
 * (`hubUrl` — `${API_URL}/hub/v1`; the same hub the legacy device events use).
 * Mirrors the HTTP transport but over a persistent connection:
 *   - `invoke('Query',  { query })`   -> `{ result }`
 *   - `invoke('Command',{ command })` -> `{ result }`
 *   - `stream('Push')`                -> `PushMessage` items (`{ push }`)
 *
 * The backend `GraphHub` speaks the exact same camelCase Query/Command/Result
 * shapes as the HTTP endpoints (verified: contracts carry explicit
 * `[JsonPropertyName]`), so the generated types are reused verbatim.
 */
export class SignalrTransport implements Transport {
  readonly kind = 'signalr' as const;

  private connection?: HubConnection;
  private starting?: Promise<void>;
  private pushSub?: ISubscription<PushMessage>;
  private readonly pushHandlers = new Set<PushHandler>();
  /** Fired whenever the hub RE-establishes after a drop (automatic reconnect). */
  private readonly reconnectedHandlers = new Set<() => void>();
  /** Which rung the live connection came up on (for the status flyout). */
  private activeTransport?: 'direct' | 'negotiated';
  /** True once we've connected at least once — a later (re)connect then fires the
   *  reconnect hooks so streams are re-subscribed; the first connect doesn't. */
  private hasConnectedOnce = false;
  private disposed = false;
  private retryLoopRunning = false;

  constructor(
    private readonly hubUrl: string,
    private readonly getAccessToken: () => Promise<string>,
    /** Whether the ladder should (still) try to hold a socket — false under the
     *  HTTP-only preference, so the background retry loop stops chasing one. */
    private readonly shouldConnect: () => boolean = () => true
  ) {}

  /** True when the hub connection is live (for the transport status indicator). */
  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  /**
   * Bring the hub up (if needed) and hand back the live connection. The legacy
   * device-events layer borrows this so it rides the SAME connection as the
   * graph CQRS instead of opening a second one to the shared `/hub/v1` hub.
   */
  async getConnection(): Promise<HubConnection> {
    await this.connect();
    return this.connection!;
  }

  /** The live rung ('direct' | 'negotiated'), or undefined when not connected. */
  get rung(): 'direct' | 'negotiated' | undefined {
    return this.isConnected ? this.activeTransport : undefined;
  }

  /** Build a hub for one rung. WebSockets-only on both; the rungs differ only in
   *  whether the negotiate round-trip runs (direct skips it — the fast path). */
  private build(skipNegotiation: boolean): HubConnection {
    const connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => this.getAccessToken(),
        // Send cookies on the negotiate/transport requests too, matching the
        // HTTP client's `withCredentials` so cookie-based auth / affinity
        // (e.g. SignalR sticky-session cookies) works across the connection.
        withCredentials: true,
        transport: HttpTransportType.WebSockets,
        skipNegotiation,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    // Ignore late callbacks from a connection the ladder has already moved past
    // (only the current one owns shared state). On (re)connect the server-side
    // streams are gone: re-open push and let consumers (device events)
    // re-subscribe theirs. On close, drop the stale subscription handle.
    connection.onreconnected(() => {
      if (connection !== this.connection) return;
      this.openPushStream();
      this.reconnectedHandlers.forEach(h => h());
    });
    // An unexpected close of the CURRENT connection means SignalR's automatic
    // reconnect gave up — start a background loop that keeps re-running the
    // ladder (with backoff) until a socket comes back.
    connection.onclose(() => {
      if (connection !== this.connection) return;
      this.pushSub = undefined;
      this.startRetryLoop();
    });
    return connection;
  }

  /**
   * Register a callback fired whenever the hub RE-establishes after a drop
   * (SignalR's automatic reconnect). The device-events layer uses this to
   * re-subscribe its server stream, which a reconnect silently drops. Returns
   * an unsubscribe.
   */
  onReconnected(handler: () => void): Unsubscribe {
    this.reconnectedHandlers.add(handler);
    return () => {
      this.reconnectedHandlers.delete(handler);
    };
  }

  async connect(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) return;
    // Collapse concurrent connect() calls onto one ladder run.
    if (!this.starting) {
      this.starting = this.connectLadder().finally(() => {
        this.starting = undefined;
      });
    }
    await this.starting;
  }

  /**
   * Escalation ladder: try a direct (skip-negotiate) WebSocket first, then a
   * negotiated one. A raw WS upgrade can hang rather than fail, so each rung is
   * time-boxed. Throws a LegacyError if neither comes up, so OneService can fall
   * back to HTTP (in `auto`) — mirroring PowerToys' Direct → Negotiated → HTTP.
   */
  private async connectLadder(): Promise<void> {
    let lastError: unknown;
    for (const skipNegotiation of [true, false]) {
      this.teardownConnection();
      const connection = this.build(skipNegotiation);
      this.connection = connection;
      try {
        await withTimeout(connection.start(), CONNECT_TIMEOUT_MS);
        this.activeTransport = skipNegotiation ? 'direct' : 'negotiated';
        this.openPushStream();
        // A (re)connect after we'd been up before means the server streams were
        // lost — let consumers (device events) re-subscribe. The first-ever
        // connect skips this: subscribers subscribe themselves.
        if (this.hasConnectedOnce) {
          this.reconnectedHandlers.forEach(h => h());
        }
        this.hasConnectedOnce = true;
        return;
      } catch (e) {
        lastError = e;
        // fall through to the next rung
      }
    }
    this.teardownConnection();
    throw new LegacyError({
      message: (lastError as Error)?.message,
      cause: lastError,
    });
  }

  /**
   * Drop the current connection and its push stream between rungs. The stop is
   * fire-and-forget: a rung that timed out may have a `start()` still in flight,
   * and awaiting `stop()` on it can hang — which would block escalation to the
   * next rung. The stale-connection guards in `build()` keep its late callbacks
   * from touching shared state after we've moved on.
   */
  private teardownConnection(): void {
    const conn = this.connection;
    this.connection = undefined;
    this.activeTransport = undefined;
    try {
      this.pushSub?.dispose();
    } catch {
      // ignore
    }
    this.pushSub = undefined;
    if (conn) void conn.stop().catch(() => {});
  }

  /**
   * Background reconnect loop: after the socket fully drops (automatic reconnect
   * exhausted), keep re-running the ladder with exponential backoff until a
   * socket comes back — so "degraded" HTTP-only mode is temporary and recovers
   * without waiting for the next query. Serialized by a flag; stops on connect,
   * dispose, or when the HTTP-only preference no longer wants a socket.
   */
  private startRetryLoop(): void {
    if (this.retryLoopRunning) return;
    this.retryLoopRunning = true;
    void this.retryLoop();
  }

  private async retryLoop(): Promise<void> {
    let attempt = 0;
    try {
      while (!this.disposed && !this.isConnected && this.shouldConnect()) {
        const seconds = Math.min(MAX_BACKOFF_S, 2 ** Math.min(attempt, 5));
        attempt += 1;
        await delay(seconds * 1000);
        if (this.disposed || this.isConnected || !this.shouldConnect()) return;
        try {
          await this.connect();
        } catch {
          // still down — back off and retry
        }
      }
    } finally {
      this.retryLoopRunning = false;
    }
  }

  query<R extends QueryResult = QueryResult>(
    query: Query,
    signal?: AbortSignal
  ): Promise<R> {
    return this.invoke<R>('Query', { query }, signal);
  }

  command<R extends CommandResult = CommandResult>(
    command: Command,
    signal?: AbortSignal
  ): Promise<R> {
    return this.invoke<R>('Command', { command }, signal);
  }

  /** Ensure connected, invoke the hub method, then surface any GraphError. */
  private async invoke<R>(
    method: 'Query' | 'Command',
    arg: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<R> {
    // Honor cancellation up front and let it propagate as an AbortError.
    if (signal?.aborted) throw abortError();

    // connect() throws a LegacyError on connection failure; let it propagate.
    await this.connect();

    let res: { result: R };
    try {
      // SignalR's `invoke` has no AbortSignal, so race it against abort. The
      // server call may still complete, but the caller's promise rejects
      // promptly — enough for React Query / route-change cancellation.
      res = await raceAbort(
        this.connection!.invoke<{ result: R }>(method, arg),
        signal
      );
    } catch (e) {
      // Preserve cancellation; wrap real hub/connection failures as a
      // LegacyError so `.code` stays meaningful.
      if (isAbortError(e)) throw e;
      throw new LegacyError({ message: (e as Error)?.message, cause: e });
    }

    // The hub returns Graph errors inside a resolved `{ result }`, same as HTTP.
    return throwIfGraphError(res.result);
  }

  subscribePush(handler: PushHandler): Unsubscribe {
    this.pushHandlers.add(handler);
    // If we're already connected, make sure the stream is running; otherwise
    // connect() (called for the first query/command, or explicitly) opens it.
    if (this.connection?.state === HubConnectionState.Connected) {
      this.openPushStream();
    }
    return () => {
      this.pushHandlers.delete(handler);
    };
  }

  private openPushStream(): void {
    const connection = this.connection;
    if (!connection || connection.state !== HubConnectionState.Connected)
      return;
    if (this.pushSub) return; // already streaming
    if (this.pushHandlers.size === 0) return; // nobody listening yet

    this.pushSub = connection.stream<PushMessage>('Push').subscribe({
      next: (msg: PushMessage) => {
        const push: Push | undefined = msg?.push;
        if (push) this.pushHandlers.forEach(h => h(push));
      },
      error: () => {
        this.pushSub = undefined;
      },
      complete: () => {
        this.pushSub = undefined;
      },
    });
  }

  dispose(): void {
    this.disposed = true;
    try {
      this.pushSub?.dispose();
    } catch {
      // ignore
    }
    this.pushSub = undefined;
    void this.connection?.stop();
    this.connection = undefined;
    this.activeTransport = undefined;
  }
}

/** Cap on the exponential backoff between background reconnect attempts. */
const MAX_BACKOFF_S = 30;

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** A raw WebSocket upgrade can hang instead of failing; time-box each rung so
 *  the ladder escalates promptly. */
const CONNECT_TIMEOUT_MS = 8_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(
      () => reject(new Error('connect timeout')),
      ms
    ) as unknown as number;
    promise.then(
      value => {
        clearTimeout(id);
        resolve(value);
      },
      error => {
        clearTimeout(id);
        reject(error);
      }
    );
  });
}

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

/**
 * Reject `promise` as soon as `signal` aborts (leaving the underlying work to
 * settle on its own). Returns `promise` unchanged when there's no signal.
 */
function raceAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      value => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      error => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}
